import "./nonce.js";
//CacheProvider & createCache needed only when using nonce and CSP
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { createRoot } from "react-dom/client";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LicenseInfo } from "@mui/x-license";
import "dayjs/locale/en-nz";

import { ListingFilterProvider, PortalFramework, Settings } from "@sal/portal";
import { Theme as PortalFrameworkTheme } from "@sal/portal-theme-default";
import Routes from "./Routes";

import { CssBaseline, ThemeProvider } from "@mui/material";
import { installAccountHeaderInterceptor, seedAccountIdFromUrlOrStorage } from "code/activeAccount";
import { installPerTabAuthStorageIsolation } from "code/perTabAuthStorage";
import { validationHelper } from "code/validationHelper";
import { SessionExpiryProvider } from "components/SessionExpiryProvider";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { useHotkeys } from "react-hotkeys-hook";
import { z } from "zod";
import logo from "./images/interislanderLogo.svg";
import { keyboardShortcuts } from "./keyboardShortcuts";
import { cvPortalTheme } from "./Theme";

// Isolate SAL's permissions cache ("salauth") to per-tab storage before anything reads it, so two
// tabs on different accounts don't clobber each other's permissions. Must run first.
installPerTabAuthStorageIsolation();
// Attach the active-account header (X-Cv2-Account) to API requests before anything fetches.
installAccountHeaderInterceptor();
// Seed the active account from the URL or localStorage so the very first requests carry it.
seedAccountIdFromUrlOrStorage();

import "@fontsource/inter/400.css";
import "@fontsource/inter/700.css";

const pfSettings: Settings = {
	logoUrl: logo,
	loginTitle: "Welcome to the CV Portal",
	defaultColorMode: "light",
	useAdapterDayjs: true,
	useKeyboardShortcutsHook: useHotkeys,
	keyboardShortcuts: keyboardShortcuts
};
LicenseInfo.setLicenseKey(process.env.MUI_X_PRO_LICENSE_KEY || "");

const styleCache = createCache({
	key: "css",
	nonce: window.nonce,
	prepend: true
});

let minMax = require("dayjs/plugin/minMax");
dayjs.extend(minMax);
dayjs.extend(updateLocale);
dayjs.updateLocale("en-nz", {
	weekStart: 1
});
dayjs.locale("en-nz");

let container = document.getElementById("root");

if (!container) {
	console.warn("Element with ID 'root' not found. Creating a new div element for the React root.");
	container = document.createElement("div");
	container.id = "root"; // Optionally assign an ID
	document.body.appendChild(container); // Append it to the body
}

const root = createRoot(container);
z.config({ customError: validationHelper });

root.render(
	<CacheProvider value={styleCache}>
		<PortalFramework settings={pfSettings}>
			<PortalFrameworkTheme>
				<ThemeProvider theme={cvPortalTheme}>
					<CssBaseline />
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-nz">
						<ListingFilterProvider>
							<SessionExpiryProvider>
								<Routes />
							</SessionExpiryProvider>
						</ListingFilterProvider>
					</LocalizationProvider>
				</ThemeProvider>
			</PortalFrameworkTheme>
		</PortalFramework>
	</CacheProvider>
);
